import { WalletOptionsProps } from '../components/ConnectExistingWallet';
import { PayWithGasContentProps } from '../components/PayWithGasContainer';

export interface ContentData {
  title?: string;
  description?: string;
  buttonName?: string;
  breadcrumbsLabel?: string;
  agreeTermsAndConditionsMessage?: string;
  additionalTermsAndConditionsLabel?: string;
  privacyPolicyLabel?: string;
  termsandConditionsLabel?: string;
  titleImageURL?: string;
  termsSeparatorText?: string;
}

export enum ClaimContainerType {
  CONNECT_WALLET = 'CONNECT_WALLET',
  ENTER_CODE = 'ENTER_CODE',
  LOADING = 'LOADING',
  SUCCESS = 'SUCCESS',
  OTP = 'OTP',
  ERROR = 'ERROR',
  EXISTING_WALLET = 'EXISTING_WALLET',
  RECOVERY_CODE = 'RECOVERY_CODE',
  CHECK_TOKEN_GATING = 'CHECK_TOKEN_GATING',
  NOT_ELIGIBLE_TOKEN_GATING = 'NOT_ELIGIBLE_TOKEN_GATING',
  PAY_WITH_GAS = 'PAY_WITH_GAS',
  CLAIM_ERROR = 'CLAIM_ERROR'
}

export interface ContentUpdateProps {
  title?: string;
  description?: string;
  seriesName?: string;
  CTA?: string;
}

export interface CopyUpdateProps {
  title?: string;
  description?: string;
  CTA?: string;
}

export interface ContentUpdateTokenGatingNotEligibleProps {
  description?: string;
  walletLimitReachedError?: string;
  notEligibleError?: string;
  pendingTransactionError?: string;
}

export interface ClaimErrorContentProps {
  title?: string;
}

interface ContentProps {
  walletLogo?: string;
  ClaimCodeContent?: ContentData;
  SuccessContent?: ContentData;
  ConnectWalletContent?: ContentData;
  LoaderContent?: ContentUpdateProps;
  RecoveryCodeModal?: {
    description?: string;
  };
  TokenGatingContent?: ContentUpdateProps;
  TokenGatingNotEligibleContent?: ContentUpdateTokenGatingNotEligibleProps;
  ClaimWithGasContent?: PayWithGasContentProps;
  ClaimErrorContent?: ClaimErrorContentProps;
}

interface LinkProps {
  logoUrl?: string;
  termsUrl: string;
  privacyUrl?: string;
  additionalTermsUrl?: string;
  viewTokenTrackerURL?: boolean;
  doNotHaveWalletURL?: string;
}

export const socialAuthOptions = [
  'google',
  'apple',
  'facebook',
  'discord',
  'line',
  'x',
  'coinbase',
  'farcaster',
  'telegram',
  'github',
  'twitch',
  'steam'
] as const;

export type SocialAuthOption = (typeof socialAuthOptions)[number];

export type ChainName = 'ethereum' | 'polygon' | 'goerli' | 'mumbai' | 'sepolia' | 'base-sepolia-testnet' | 'base';

export interface ClaimModalLayoutProps {
  open: boolean;
  claimStatus: string;
  currentModalState: ClaimContainerType;
  code: string;
  error: string;
  inValidCode?: string | null;
  address: string;
  isEnterClaimCode?: boolean;
  email: string;
  otp: string;
  loaderTitle: string;
  loaderDescription: string;
  recoveryCode: string;
  walletOptions: WalletOptionsProps;
  tokenTrackerURL: string;
  checked: boolean;
  termsUrl: string;
  showLoader: boolean;
  logoUrl?: string;
  viewTokenTrackerURL?: boolean;
  additionalTermsUrl?: string;
  privacyUrl?: string;
  content?: ContentProps;
  tokenName?: string;
  tokenGatingName?: string;
  tokenGatingError?: string;
  showDisconnectButton?: boolean;
  showBuyButton?: boolean;
  isTokenGating?: boolean;
  price?: string;
  validation?: 'clear' | 'no_balance' | 'wrong_network';
  networkName?: string;
  currency?: string;
  onClickSwitchNetwork: () => void;
  onClickDoNotHaveWallet: () => void;
  onChangeCheck: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onChangeEmail: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onChangeCode: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onChangeRecoveryCode: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onChangeOTP: (event: string) => void;
  onClickMetamask: () => void;
  onClickWalletConnect: () => void;
  onClickContinueWithEmail: () => void;
  onClickRecoveryCode: () => void;
  onClickExistingWallet: () => void;
  onCloseModal: (event: ClaimContainerType) => void;
  handleSuccessRedirection?: () => void;
  handleRetry: () => void;
  onClickBreadcrumb: (event: string) => void;
  onClickContinueCheckToken: () => void;
  onClickBuyToken?: () => void;
  onClickDisconnectWallet: () => void;
  onClickClaimWithGas: () => void;
  onClickContinueWithSocial: (type: SocialAuthOption) => void;
  onClickSkip: () => void;
}

export interface ClaimConfiguration {
  chainId: number;
  crossmintApiKey?: string;
  orgId: string;
  crossmintEnv?: string;
  paperClientId?: string;
  paperNetworkName?: any;
}

export interface BalanceData {
  native: number;
  nonNative: number;
}

export interface SupportedNetworksData {
  chainID: number;
  id: string;
  isTestnet: boolean;
  name: string;
}

interface TokenGatingConfigProps {
  groupId: string;
  ruleId: string;
}

export enum SaleType {
  TokenGating = 'TokenGating',
  SkipTokenGating = 'SkipTokenGating',
  CustomCode = 'CustomCode',
  NoCode = 'NoCode',
  BuyNow = 'BuyNow',
  SkipAPI = 'SkipAPI'
}

export interface WalletOptionsMixersProps {
  enableMetamask: boolean;
  enableWalletConnect: boolean;
  enableEmailWallet: boolean;
}

export interface PaymentOptionsProps {
  creditCard?: boolean;
  walletConnect?: boolean;
  wire?: boolean;
}

export interface ClaimTokenModalWithoutMixersProps {
  open: boolean;
  firstName?: string;
  lastName?: string;
  userEmail?: string;
  loginWithPersonalInformation?: boolean;
  claimCode?: string;
  listingId?: string;
  config: ClaimConfiguration;
  tokenGatingConfig?: TokenGatingConfigProps;
  link: LinkProps;
  content?: ContentProps;
  skipClaimModal?: boolean;
  skipSuccessModal?: boolean;
  skipConfirmClaimModal?: boolean;
  isPaperWallet?: boolean;
  tokenName?: string;
  showDisconnectButton?: boolean;
  showBuyButton?: boolean;
  price?: string;
  isClaimWithGas?: boolean;
  claimType: 'TokenGating' | 'SkipTokenGating' | 'CustomCode' | 'NoCode' | 'BuyNow';
  skipAPI?: boolean;
  mixersConfig?: {
    lotId: string;
    walletOptions: WalletOptionsMixersProps;
    paymentId?: string;
    discountCode?: string;
    paymentOptions: PaymentOptionsProps;
    checkOutApiKey?: string;
    errorPageUrl: string;
    successPageUrl: string;
    invoiceId?: string;
    isWeb2Login?: boolean;
    skipSignature?: boolean;
    breadCrumbs?: string[];
    isAutoFillCountryCode?: boolean;
    onClickGoToMarketPlace?: () => void;
  };
  onCloseModal: () => void;
  onSuccess?: (hash?: string) => void;
  onClickBuyToken?: () => void;
}

export type BuyNowClaimTokenModalProps = Omit<ClaimTokenModalWithoutMixersProps, 'mixersConfig'> & {
  mixersConfig: {
    lotId: string;
    walletOptions: WalletOptionsMixersProps;
    paymentId?: string;
    discountCode?: string;
    paymentOptions: PaymentOptionsProps;
    checkOutApiKey?: string;
    errorPageUrl: string;
    successPageUrl: string;
    invoiceId?: string;
    isWeb2Login?: boolean;
    skipSignature?: boolean;
    onClickGoToMarketPlace?: () => void;
  };
  claimType: 'BuyNow';
};

export type ClaimTokenModalProps = ClaimTokenModalWithoutMixersProps | BuyNowClaimTokenModalProps;

export interface MetaData {
  name: string;
  description: string;
  url: string;
  icons: string[];
}

export interface CheckoutContainerProps {
  show: boolean;
  success?: boolean;
  token?: string;
  email?: string;
  logoUrl?: string;
  config?: ClaimConfiguration;
  mixersConfig?: {
    lotId: string;
    walletOptions: WalletOptionsMixersProps;
    paymentId?: string;
    discountCode?: string;
    paymentOptions: PaymentOptionsProps;
    checkOutApiKey?: string;
    errorPageUrl: string;
    successPageUrl: string;
    invoiceId?: string;
    isWeb2Login?: boolean;
    skipSignature?: boolean;
    breadCrumbs?: string[];
    isAutoFillCountryCode?: boolean;
  };
  listingId?: string;
  walletDetails?: any;
  uri?: string;
  walletConnectProjectId?: string;
  clientId?: string;
  paymentId?: string;
  activeChain?: 'ethereum' | 'polygon' | 'goerli' | 'mumbai' | 'sepolia';
  disconnect?: () => void;
  refetchBalance?: () => void;
  onClickConnectWallet?: () => void;
  onClickGoToMarketPlace?: () => void;
}

export interface PaymentMixersSuccessProps {
  onClickGoToMarketPlace: () => void;
}

export interface NetlifyProps {
  ipAddress: string;
  clientConnectionIp: string;
  geo: Geo;
}

export interface Geo {
  city: string;
  country: Country;
  subdivision: Subdivision;
  timezone: string;
  latitude: number;
  longitude: number;
}

export interface Country {
  code: string;
  name: string;
}

export interface Subdivision {
  code: string;
  name: string;
}
